// areaRadialTools.js — Bespoke draw interactions for AREA center+radius rules.
// AREA6, AREA14, AREA15, AREA16, AREA19
//
// AREA6/15/16/19: 2 pts (center + radius/arc-open direction).
// AREA14:         3 pts (center + radius of circle 1 + radius of circle 2).
//
// Sketch preview: growing dashed circle ring for each radius point.

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import { Style, Stroke } from 'ol/style';
import Feature from 'ol/Feature';
import { Circle as OLCircleGeom } from 'ol/geom';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createMultiPointCollector } from './multiPointCollector';

const RING_STYLES = [
    new Style({ stroke: new Stroke({ color: '#3b82f6', width: 2, lineDash: [6, 3] }) }),
    new Style({ stroke: new Stroke({ color: '#f59e0b', width: 2, lineDash: [6, 3] }) }),
];

// P1 = center (anchor role); every subsequent point is a radius/direction pick (direction
// role — analogous to a directional arrow's target click, since each one is the user actively
// aiming at how far/which-way a ring should extend).
function roleForIndex(i) { return i === 0 ? 'anchor' : 'direction'; }

function makeRadialCollector(map, { targetCount, onFinish }) {
    return createMultiPointCollector(map, {
        minCount: targetCount,
        maxCount: targetCount,
        onFinish,
        roleForIndex,
        styleResolver: (f) => RING_STYLES[f.get('_ring') ?? 0] ?? RING_STYLES[0],
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            const center3857 = collected3857[0];
            if (!center3857) return;

            function addRing(edgeCoord3857, ringIdx) {
                const dx = edgeCoord3857[0] - center3857[0];
                const dy = edgeCoord3857[1] - center3857[1];
                const radius = Math.sqrt(dx * dx + dy * dy);
                const rf = new Feature(new OLCircleGeom(center3857, radius));
                rf.set('_type', 'ring');
                rf.set('_ring', ringIdx);
                previewSource.addFeature(rf);
            }

            // Committed radius rings
            for (let i = 1; i < collected3857.length; i++) addRing(collected3857[i], i - 1);

            // Live cursor ring
            if (cursorCoord3857 && collected3857.length < targetCount) {
                addRing(cursorCoord3857, collected3857.length - 1);
            }
        },
    });
}

// 2-pt radial factory (center + radius)
function make2ptRadial(drawRule) {
    return (map, opts) => makeRadialCollector(map, { targetCount: 2, onFinish: opts.onFinish });
}

registerDrawInteraction(DrawRules.AREA6,  make2ptRadial(DrawRules.AREA6));
registerDrawInteraction(DrawRules.AREA15, make2ptRadial(DrawRules.AREA15));
registerDrawInteraction(DrawRules.AREA16, make2ptRadial(DrawRules.AREA16));
registerDrawInteraction(DrawRules.AREA19, make2ptRadial(DrawRules.AREA19));

// AREA14 — 3-pt bullseye (center + 2 radii)
registerDrawInteraction(DrawRules.AREA14, (map, opts) =>
    makeRadialCollector(map, { targetCount: 3, onFinish: opts.onFinish }));

